import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { createLogger } from '@/utils/logger.js';
import { PageLoader } from '../components/PageLoader';

import logoSvg from '../assets/logos/Q-full-logo-dark.svg?raw';

import profileIcon from '../assets/login/user-icon.svg';
import passwordIcon from '../assets/login/password-icon.svg';
import eyeOpenIcon from '../assets/login/eye-open.svg';
import eyeClosedIcon from '../assets/login/eye-closed.svg';

import styles from './LoginPage.module.css';

const logger = createLogger('Shell::Login');

function LoginPage() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [incorrectLogin, setIncorrectLogin] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { login, loading } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation('shell');

    const handleSubmit = async (e) => {
        e.preventDefault();
        logger.info('Login attempt with:', username);

        setIsSubmitting(true);
        setIncorrectLogin(false);

        const success = await login({ username, password });
        logger.info('Login result:', success);

        if (success) {
            navigate('/');
        } else {
            setIsSubmitting(false);
            setIncorrectLogin(true);
        }
    };

    return (
        <div>
            {isSubmitting && (
                <div className={styles.loadingOverlay}>
                    <PageLoader fullPage={false} transparent />
                </div>
            )}
            <div className={styles.topPart} />
            <div className={styles.bottomPart} />
            <div className={styles.loginArea}>
                <div className={styles.logoWrapper}>
                    <div
                        className={styles.logo}
                        dangerouslySetInnerHTML={{ __html: logoSvg }}
                        role="img"
                        aria-label={t('login.logoAlt')}
                    />
                </div>

                <form
                    id="login-form"
                    className={styles.inputArea}
                    onSubmit={handleSubmit}
                    autoComplete="on"
                >
                    <div>
                        <img
                            className={styles.profileIcon}
                            src={profileIcon}
                            alt={t('login.emailAlt')}
                        />
                        <input
                            className={styles.usernameInput}
                            type="email"
                            placeholder={t('login.emailPlaceholder')}
                            value={username}
                            onChange={(e) => setUsername(e.target.value.trim())}
                            disabled={loading || isSubmitting}
                            autoComplete="username"
                            required
                        />
                        <div className={styles.linebreak} />
                    </div>

                    <div style={{ marginTop: '25px', position: 'relative' }}>
                        <img
                            className={styles.passwordIcon}
                            src={passwordIcon}
                            alt={t('login.passwordAlt')}
                        />
                        <input
                            className={styles.passwordInput}
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading || isSubmitting}
                            autoComplete="current-password"
                            required
                        />
                        <img
                            className={styles.eyeIcon}
                            src={showPassword ? eyeClosedIcon : eyeOpenIcon}
                            alt={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                            onClick={() => setShowPassword(!showPassword)}
                        />
                        <div className={styles.linebreak} />
                    </div>
                </form>

                <button
                    className={styles.loginBtn}
                    type="submit"
                    form="login-form"
                    disabled={loading || isSubmitting}
                >
                    <div className={styles.loginTxt}>
                        {isSubmitting ? t('login.loggingIn') : t('login.loginButton')}
                    </div>
                </button>

                {incorrectLogin && (
                    <div className={styles.incorrectTextContainer}>
                        <div className={styles.incorrectLogin}>
                            {t('login.incorrectLogin')}
                            <span className={styles.incorrectLoginInstructions}>
                                <a
                                    href="https://3dsignals.com/contact-us/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('login.needHelp')}
                                </a>
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LoginPage;